import React, { type ChangeEvent } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import { CheckboxFieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (updatedField: AssociatedField) => void) =>
	(e: ChangeEvent<HTMLInputElement>) =>
		onUpdate({
			...field,
			defaultValue: !e.target.checked
				? // We need to do this to correctly indicate empty state, another solution is polluting field logic
					null
				: {
						useCurrentTime: e.target.checked,
					},
		});

const refineDefaultValue = (defaultValue: unknown): boolean =>
	defaultValue &&
	typeof defaultValue === 'object' &&
	'useCurrentTime' in defaultValue &&
	typeof defaultValue.useCurrentTime === 'boolean'
		? defaultValue.useCurrentTime
		: false;

/**
 * Allows users to choose "Use current time" as the default value for a date-time field.
 */
export const CurrentDateTimeDefaultValue = ({
	field,
	onUpdate,
	tooltipDelay,
}: FieldConfigurationComponentProps) => {
	const intl: IntlShape = useIntl();
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;
	const useCurrentTime = refineDefaultValue(field.defaultValue);

	return (
		<CheckboxFieldConfigurationDefaultValue>
			<Tooltip
				content={
					isModifyDefaultValueDisabled
						? intl.formatMessage(messages.datetimeDefaultValueDisabledTooltip)
						: null
				}
				delay={tooltipDelay}
			>
				<Checkbox
					isChecked={useCurrentTime}
					isDisabled={isModifyDefaultValueDisabled}
					label={intl.formatMessage(messages.datetimeDefaultValueLabel)}
					onChange={onDefaultValueChange(field, onUpdate)}
				/>
			</Tooltip>
		</CheckboxFieldConfigurationDefaultValue>
	);
};
